import { z } from "zod";

/**
 * Zod input schemas — validated by the API, and the inferred types are used by
 * the web app so both sides can never drift.
 */

/** E.164 digits without the leading + (reference backend convention). */
export const phoneSchema = z
  .string()
  .regex(/^\d{7,15}$/, "Phone must be E.164 digits without the leading +");

export const bawabahCallbackSchema = z.object({
  sessionId: z.string().min(1),
});
export type BawabahCallbackInput = z.infer<typeof bawabahCallbackSchema>;

/** Store-review bypass: the email + secret code given to app reviewers. */
export const reviewLoginSchema = z.object({
  email: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(200),
});
export type ReviewLoginInput = z.infer<typeof reviewLoginSchema>;

export const createClientSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: phoneSchema,
});
export type CreateClientInput = z.infer<typeof createClientSchema>;

export const mediaKindSchema = z.enum(["Image", "Video", "Audio", "Document"]);
export type MediaKind = z.infer<typeof mediaKindSchema>;

export const locationSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  name: z.string().trim().max(200).optional(),
  address: z.string().trim().max(500).optional(),
});
export type LocationInput = z.infer<typeof locationSchema>;

/**
 * Free-form send: text, media (previously uploaded via upload-media, body =
 * caption), or a location pin. Exactly one payload kind must be present.
 */
export const sendMessageSchema = z
  .object({
    conversationId: z.uuid(),
    body: z.string().trim().min(1).max(4096).optional(),
    /** wamid of the message being replied to (WhatsApp quoted reply). */
    replyToWamid: z.string().trim().min(1).max(255).optional(),
    mediaId: z.string().min(1).optional(),
    mediaKind: mediaKindSchema.optional(),
    mediaFileName: z.string().trim().max(255).optional(),
    location: locationSchema.optional(),
  })
  .refine((v) => v.body || v.mediaId || v.location, {
    message: "Provide body, mediaId or location",
  })
  .refine((v) => !v.mediaId || v.mediaKind, {
    message: "mediaKind is required with mediaId",
  })
  .refine((v) => !(v.mediaId && v.location), {
    message: "mediaId and location are mutually exclusive",
  });
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const templateVariablesSchema = z.record(
  z.string(),
  z.string().trim().min(1).max(1024),
);

export const sendTemplateSchema = z.object({
  conversationId: z.uuid(),
  templateId: z.uuid(),
  variables: templateVariablesSchema.default({}),
});
export type SendTemplateInput = z.infer<typeof sendTemplateSchema>;

export const deliveryStatusSchema = z.enum([
  "New",
  "Ongoing",
  "Delivered",
  "Cancelled",
]);

export const shipmentSourceSchema = z.enum([
  "Manual",
  "List",
  "Label",
  "OpenWithApp",
]);
export const shipmentPaymentTypeSchema = z.enum(["COD", "Paid", "Other"]);
export const senderTypeSchema = z.enum(["Company", "Individual"]);

/**
 * Saudi National Address short code: 4 letters + 4 digits. Spaces and case
 * are normalized away ("rrrd 2929" -> "RRRD2929").
 */
export const nationalAddressSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/\s+/g, "").toUpperCase())
  .pipe(z.string().regex(/^[A-Z]{4}\d{4}$/, "Invalid national address"));

/**
 * Optional shipment facts (see ShipmentDetailsDto). Every source — the label
 * OCR, an import column, a WhatsApp link — contributes what it has; nothing
 * here is ever required of the driver. Money is in halalas, weight in grams.
 */
export const shipmentDetailsSchema = z.object({
  paymentType: shipmentPaymentTypeSchema.optional(),
  costHalalas: z.number().int().min(0).max(1_000_000_000).optional(),
  shippingCostHalalas: z.number().int().min(0).max(1_000_000_000).optional(),
  senderName: z.string().trim().min(1).max(120).optional(),
  senderType: senderTypeSchema.optional(),
  senderWebsite: z.string().trim().min(1).max(200).optional(),
  weightGrams: z.number().int().min(1).max(100_000_000).optional(),
  nationalAddress: nationalAddressSchema.optional(),
  /** Stored on the CLIENT (city), only when the client has none yet. */
  clientCity: z.string().trim().min(1).max(120).optional(),
});
export type ShipmentDetailsInput = z.infer<typeof shipmentDetailsSchema>;

/**
 * New delivery (شحنة جديدة): create/reuse a client, create the conversation
 * (= the delivery), send the first template. Either an existing clientId or a
 * new client's phone (name optional — falls back to the phone). The delivery
 * fields default from the driver profile server-side when omitted.
 */
export const startConversationSchema = shipmentDetailsSchema
  .extend({
    clientId: z.uuid().optional(),
    clientName: z.string().trim().min(1).max(120).optional(),
    clientPhone: phoneSchema.optional(),
    trackingNumber: z.string().trim().min(1).max(100).optional(),
    company: z.string().trim().min(1).max(120).optional(),
    driverName: z.string().trim().min(1).max(120).optional(),
    driverCallingPhone: phoneSchema.optional(),
    /** How the delivery was added; defaults to Manual. Bulk import forces List. */
    source: shipmentSourceSchema.default("Manual"),
    templateId: z.uuid(),
    variables: templateVariablesSchema.default({}),
  })
  .refine((v) => v.clientId || v.clientPhone, {
    message: "Provide clientId or clientPhone",
  });
export type StartConversationInput = z.infer<typeof startConversationSchema>;

/**
 * One spreadsheet row of a bulk delivery import. Mirrors the optional delivery
 * fields of `startConversationSchema` (a row always creates/reuses a client by
 * phone) plus the per-row template variables resolved from the mapped columns.
 * `rowNumber` is the 1-based source row, echoed back in the result so the UI
 * can point at the offending line.
 */
export const importDeliveryRowSchema = shipmentDetailsSchema.extend({
  rowNumber: z.number().int().min(1),
  clientName: z.string().trim().min(1).max(120).optional(),
  clientPhone: phoneSchema,
  trackingNumber: z.string().trim().min(1).max(100).optional(),
  company: z.string().trim().min(1).max(120).optional(),
  driverName: z.string().trim().min(1).max(120).optional(),
  driverCallingPhone: phoneSchema.optional(),
  variables: templateVariablesSchema.default({}),
});
export type ImportDeliveryRowInput = z.infer<typeof importDeliveryRowSchema>;

/**
 * Rows per import request. Each row sends a template to Meta, so a whole file
 * is uploaded in batches of this size — bounding the request time and giving
 * the web app something to show progress against.
 */
export const IMPORT_DELIVERIES_BATCH_SIZE = 25;

/** Bulk delivery import: one template for the batch, one delivery per row. */
export const importDeliveriesSchema = z.object({
  templateId: z.uuid(),
  rows: z
    .array(importDeliveryRowSchema)
    .min(1)
    .max(IMPORT_DELIVERIES_BATCH_SIZE),
});
export type ImportDeliveriesInput = z.infer<typeof importDeliveriesSchema>;

/**
 * Delivery info (tracking/company/driver fields) is immutable once the
 * conversation starts — only the STATUS may change afterwards.
 */
export const updateDeliverySchema = z.object({
  conversationId: z.uuid(),
  status: deliveryStatusSchema,
});
export type UpdateDeliveryInput = z.infer<typeof updateDeliverySchema>;

/** React to a message; an empty emoji removes the driver's reaction. */
export const reactToMessageSchema = z.object({
  messageId: z.uuid(),
  emoji: z.string().trim().max(16),
});
export type ReactToMessageInput = z.infer<typeof reactToMessageSchema>;

export const markReadSchema = z.object({
  conversationId: z.uuid(),
});
export type MarkReadInput = z.infer<typeof markReadSchema>;

/** Re-close a reopened (finished) conversation, freeing the client number. */
export const closeConversationSchema = z.object({
  conversationId: z.uuid(),
});
export type CloseConversationInput = z.infer<typeof closeConversationSchema>;

export const setStarredSchema = z.object({
  conversationId: z.uuid(),
  isStarred: z.boolean(),
});
export type SetStarredInput = z.infer<typeof setStarredSchema>;

/** Omitted fields are unchanged; null (or empty string for text fields) clears. */
export const updateProfileSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  company: z.string().trim().max(120).nullable().optional(),
  callingPhone: phoneSchema.nullable().optional(),
  language: z.enum(["ar", "en"]).optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const pushPermissionSchema = z.enum([
  "granted",
  "denied",
  "prompt",
  "unsupported",
]);
export type PushPermission = z.infer<typeof pushPermissionSchema>;

/**
 * What the app knows about the device it runs on, reported silently on launch
 * and whenever the notification permission or UI language changes. Every
 * field is optional: the API stores what arrives and keeps the rest.
 */
export const deviceInfoSchema = z.object({
  platform: z.enum(["ios", "android", "web"]).optional(),
  model: z.string().trim().min(1).max(120).optional(),
  osVersion: z.string().trim().min(1).max(60).optional(),
  appVersion: z.string().trim().min(1).max(60).optional(),
  pushPermission: pushPermissionSchema.optional(),
  /** The UI language the app is actually showing. */
  language: z.enum(["ar", "en"]).optional(),
  /** ISO 3166-1 alpha-2 region of the device locale — country fallback. */
  region: z
    .string()
    .trim()
    .length(2)
    .transform((value) => value.toUpperCase())
    .optional(),
});
export type DeviceInfoInput = z.infer<typeof deviceInfoSchema>;

export const pagedQuerySchema = z.object({
  SkipCount: z.coerce.number().int().min(0).default(0),
  MaxResultCount: z.coerce.number().int().min(1).max(200).default(50),
  Sorting: z.string().optional(),
});
export type PagedQueryInput = z.infer<typeof pagedQuerySchema>;

export const conversationsQuerySchema = pagedQuerySchema.extend({
  Filter: z.string().optional(),
  Starred: z.coerce.boolean().optional(),
  /** "Undelivered" is a pseudo-status covering New + Ongoing. */
  DeliveryStatus: z
    .union([deliveryStatusSchema, z.literal("Undelivered")])
    .optional(),
  /**
   * true = only archived conversations (closed for 24h+); absent/false = only
   * active ones and those closed less than 24h ago.
   */
  Archived: z.coerce.boolean().optional(),
});
export type ConversationsQueryInput = z.infer<typeof conversationsQuerySchema>;

export const threadQuerySchema = z.object({
  SkipCount: z.coerce.number().int().min(0).default(0),
  MaxResultCount: z.coerce.number().int().min(1).max(200).default(100),
});
export type ThreadQueryInput = z.infer<typeof threadQuerySchema>;
